import React, { useState } from 'react';
import classnames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Collapse from '@material-ui/core/Collapse';
import { ReactComponent as BusinessIcon } from 'assets/images/ion_business.svg';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { shortDate } from 'helpers/_date-helpers';
import styles from './styles';
import photoPlaceholder from 'assets/images/user-photo-placeholder.svg';
import CloseIcon from 'assets/images/close-icon.svg';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { generateHoursObject } from 'helpers/helpers';
import { useNavigate } from 'react-router-dom';
import modalsPageActions from 'store/modals/modalsPageActions';
import { updateSheets } from 'store/schedule/schedulePageOperation';
import { format } from 'date-fns';
import { useGetEquipmentInfo } from 'pages/ScheduleV6/components/hooks/useGetEquipmentInfo';
import { useTimeSettingsFormat } from 'common/useTimeSettingsFormat';
import { useCanEditWorklog } from 'common/hooks/use-can-edit-worklog';
import { compareWorkersCrewLeaderThenAlpha } from 'helpers/_helpers';
import { useRemoveSheet } from 'pages/ScheduleV6/components/hooks/useRemoveSheet';
import { getSheetEventTitle } from 'pages/ScheduleV6/utils';

const SheetCard = ({ classes, sheet, sheets, openSnackbar, sheetsWithoutForemanIds }) => {
  const selectedDate = useSelector((state) => state.schedule.selectedDate);
  const getTimeFormatHook = useTimeSettingsFormat();
  const workersData = useSelector((state) => state.schedule.resources.workers);
  const { getEquipmentInfo } = useGetEquipmentInfo(sheets.flatMap((el) => el.equipment));
  const locations = useSelector((state) => state.personalProfile.activeLocations);

  const [isExpanded, setIsExpanded] = useState(false);

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const { removeSheet } = useRemoveSheet({ sheetId: sheet._id });

  const openDeleteModal = (e) => {
    e.stopPropagation();
    dispatch(
      modalsPageActions.openModal({
        name: 'remove',
        data: 'Are you sure you want to remove this project from the schedule?',
        onSubmit: (e) =>
          removeSheet()
            .then(() => dispatch(modalsPageActions.closeAllModals()))
            .then(() => dispatch(updateSheets())),
      })
    );
  };

  const handleExpandClick = (e) => {
    e.stopPropagation();
    setIsExpanded((prev) => !prev);
  };


  const getJobNumber = () => sheet.project.jobNumber;

  const getFormattedHoursRange = () => {
    const { createdAt, hours } = sheet;
    if (!hours) return;
    const { hourStart, minuteStart, amPmStart, dayStart, hourEnd, minuteEnd, amPmEnd, dayEnd } =
      generateHoursObject(hours.start, hours.end, createdAt, getTimeFormatHook);

    const startObj = [null, hourStart, minuteStart, amPmStart];
    const endObj = [null, hourEnd, minuteEnd, amPmEnd];

    const start = `${getTimeFormatHook(startObj).formattedTime} ${shortDate(
      createdAt,
      dayStart,
      getTimeFormatHook().localDayMonth
    )}`;
    const end = `${getTimeFormatHook(endObj).formattedTime} ${shortDate(
      createdAt,
      dayEnd,
      getTimeFormatHook().localDayMonth
    )}`;

    return `${start} \u2013 ${end}`;
  };

  const openWorklogDetails = () => {
    navigate(`/schedule/${format(selectedDate, 'yyyy-MM-dd')}?sheetId=${sheet._id}`);;
  };

  const hasEditAccess = useCanEditWorklog(sheet);

  return (
    <>
      <Card
        className={classnames(
          classes.card,
          sheetsWithoutForemanIds.includes(sheet._id) && classes.missingCL
        )}
        onClick={openWorklogDetails}
      >
        <Grid container direction={'column'}>
          <div
            className={classnames(
              classes.shiftColorWrapper,
              sheet.timeOfDay === 'Day' ? classes.dayShift : classes.nightShift
            )}
          >
            <Grid
              item
              xs={12}
              className={classnames(classes.paddingBottom, classes.inlineFlex, classes.flexHeader)}
            >
              <div className={classes.sheetLocation}>
                <BusinessIcon className={classnames(classes.locationIcon)} />
                {locations?.find(({ _id }) => _id === sheet.tenantLocationId)?.name}
              </div>
              <div className={classnames(classes.inlineFlex, classes.selfCenter, classes.justifyEnd)}>
                {sheet.forceAccount && sheet.forceAccount !== 'No' && (
                  <div className={classes.flexWrapper}>
                    <Typography
                      variant="body2"
                      className={classnames(classes.forceAccount, classes.marginBottomNormal)}
                    >
                      T&M: {sheet.forceAccount}
                    </Typography>
                  </div>
                )}
              </div>
              <div className={classnames(classes.inlineFlex, classes.buttons)}>
                {(Boolean(sheet.workers?.length) ||
                  Boolean(sheet.equipment?.length) ||
                  Boolean(sheet.notes)) && (
                  <IconButton
                    className={classnames(classes.expand, {
                      [classes.expandOpen]: isExpanded,
                    })}
                    aria-expanded={isExpanded}
                    aria-label="Show more"
                    disableRipple
                    disableFocusRipple
                    onClick={handleExpandClick}
                  >
                    <ExpandMoreIcon fontSize="medium" />
                  </IconButton>
                )}

                {hasEditAccess && <Button
                  color="secondary"
                  className={classes.deleteButton}
                  onClick={openDeleteModal}
                  disableRipple
                >
                  <img src={CloseIcon} alt="delete" />
                </Button>}
              </div>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" color="textPrimary" className={classes.paddingBottom}>
                {getSheetEventTitle(sheet, 'short')}
              </Typography>
              <div
                className={classnames(
                  // classes.inlineFlex,
                  classes.paddingBottom,
                  classes.itemsFlexStart
                )}
              >
                <Typography variant="body1" color="textSecondary" className={classnames(classes.mediumFontWeight, classes.paddingBottomSmall)}>
                  {sheet.project.contractor && sheet.project.contractor.name}
                </Typography>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  className={classnames(
                    classes.mediumFontWeight,
                    sheet.locations && sheet.locations.length ? classes.paddingBottomSmall : null
                  )}
                >
                  #{getJobNumber()}
                </Typography>
              </div>
            </Grid>
            {sheet?.hours?.start && sheet?.hours?.start && (
              <Grid item xs={12} className={classes.paddingBottom}>
                <Typography
                  variant="body1"
                  color="textPrimary"
                  className={classnames(classes.regularFontWeight, classes.paddingBottomSmall)}
                >
                  {getFormattedHoursRange()}
                </Typography>
              </Grid>
            )}
          </div>

            <Collapse in={isExpanded} timeout="auto" unmountOnExit className={classes.bottomWrapper}>
              <Grid>
                {Boolean(sheet.notes) && (
                  <Grid item className={classes.paddingBottom}>
                    <Typography
                      variant="body1"
                      color="textSecondary"
                      className={classes.marginBottomSmall}
                    >
                      Note:
                    </Typography>
                    <Typography
                      variant="body1"
                      color="textPrimary"
                      className={classnames(classes.notesText, classes.marginBottom)}
                    >
                      {sheet.notes}
                    </Typography>
                  </Grid>
                )}
                {Boolean(sheet.workers.length) && (
                  <Grid item>
                    <Typography
                      variant="body1"
                      color="textSecondary"
                      className={classes.marginBottomSmall}
                    >
                      People:
                    </Typography>
                    <div className={classnames(classes.marginBottomSmall)}>
                      {sheet.workers
                        .map((w) => (workersData || []).find((worker) => worker._id === w._id))
                        .filter((w) => !!w)
                        .sort(compareWorkersCrewLeaderThenAlpha)

                        .map((worker) => (
                          <div
                            key={worker._id}
                            className={classnames(classes.marginBottom, classes.itemWrapper)}
                          >
                            <div
                              className={classnames(
                                classes.flexBox,
                                classes.justifyContentStart,
                                classes.marginBottomSmall
                              )}
                            >
                              <div
                                className={classnames(
                                  classes.photoWrapper,
                                  classes[`${worker.profile.shifts.timeOfDay.toLowerCase()}Border`]
                                )}
                              >
                                <img src={photoPlaceholder} className={classes.photo} />
                              </div>
                              <Typography
                                variant="body1"
                                color="textPrimary"
                                className={classnames(
                                  classes.username,
                                  classes[worker.profile.shifts.timeOfDay.toLowerCase()]
                                )}
                              >
                                {worker.username}
                              </Typography>
                            </div>
                          </div>
                        ))}
                    </div>
                  </Grid>
                )}
                {Boolean(sheet.equipment.length) && (
                  <Grid item>
                    <Typography
                      variant="body1"
                      color="textSecondary"
                      className={classes.marginBottomSmall}
                    >
                      Equipment:
                    </Typography>
                    <div className={classnames(classes.marginBottomSmall)}>
                      {sheet.equipment.map((item) => (
                        <div
                          key={item._id}
                          className={classnames(
                            classes.marginBottom,
                            classes.itemWrapper,
                            classes.marginBottomSmall
                          )}
                        >
                          <div
                            className={classnames(
                              classes.equipmentBlock,
                              getEquipmentInfo(item._id, 'className')
                            )}
                          >
                            <div
                              className={classes.equipmentNumber}
                              style={{ backgroundColor: getEquipmentInfo(item._id, 'color') }}
                            >
                              {getEquipmentInfo(item._id, 'number')}
                            </div>
                            <div
                              className={classes.equipmentName}
                              style={{ color: getEquipmentInfo(item._id, 'color') }}
                            >
                              {getEquipmentInfo(item._id, 'name')}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Grid>
                )}
              </Grid>
            </Collapse>
          {/* </div> */}
        </Grid>
      </Card>
    </>
  );
};

export default withStyles(styles)(SheetCard);
